/**
 * seriesInfoCache — localStorage cache for xtream get_series_info responses.
 *
 * Cache key scope: server host + series_id.
 *   - Shared across all users of the same server (same episode list/metadata).
 *   - Different servers use different keys.
 *
 * Access check: checkSeriesAccess(seriesId, userSeriesStreams) ensures the
 *   series is in the user's current series list before showing details.
 *
 * TTL: 24 hours.
 * Inflight dedup: concurrent callers for the same key share one request.
 */

import xtreamAPI, { XtreamContent, XtreamSeriesInfo } from '../api/xtreamAPI';

const TTL_MS = 24 * 60 * 60 * 1000;

const inflight = new Map<string, Promise<XtreamSeriesInfo>>();

function cacheKey(host: string, seriesId: string): string {
  return `ott_series_info_v1_${host}_${seriesId}`;
}

function read(key: string): XtreamSeriesInfo | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: XtreamSeriesInfo; ts: number };
    if (Date.now() - ts > TTL_MS) { localStorage.removeItem(key); return null; }
    return data;
  } catch { return null; }
}

function write(key: string, data: XtreamSeriesInfo): void {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/**
 * Drop-in replacement for `xtreamAPI.getSeriesInfo(seriesId)`.
 * Throws if the underlying request fails (mirrors xtreamAPI behaviour).
 */
export async function getCachedSeriesInfo(seriesId: string): Promise<XtreamSeriesInfo> {
  const host = xtreamAPI.getHost();
  const key = cacheKey(host, seriesId);

  const hit = read(key);
  if (hit !== null) return hit;

  if (inflight.has(key)) return inflight.get(key)!;

  const promise = xtreamAPI.getSeriesInfo(seriesId).then((data) => {
    write(key, data);
    inflight.delete(key);
    return data;
  }).catch((err) => {
    inflight.delete(key);
    throw err;
  });

  inflight.set(key, promise);
  return promise;
}

/**
 * Check whether a series is accessible to the current user.
 * Pass the result of getCachedSeriesStreams() as `userStreams`.
 */
export function checkSeriesAccess(seriesId: string, userStreams: XtreamContent[]): boolean {
  const sid = String(seriesId);
  return userStreams.some(s => String(s.series_id ?? s.stream_id ?? '') === sid);
}

/** Evict a single series entry. */
export function evictSeriesInfoCache(seriesId: string): void {
  const key = cacheKey(xtreamAPI.getHost(), seriesId);
  try { localStorage.removeItem(key); } catch {}
}

export function clearSeriesInfoInflight(): void {
  inflight.clear();
}
